import React, { useState } from "react";
import "./filters.css";
import { Card, Col, Form, Alert, Spinner, Button, Row } from "react-bootstrap";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
} from "recharts";
import { IoMdClose } from "react-icons/io";
import { AiOutlineCloseCircle } from "react-icons/ai";
import { BiAnalyse } from "react-icons/bi";
import { industriesNew } from "../../dropdown-options/industries";
import { entityTypes } from "../../dropdown-options/entity-types";
import { reasonsForShutdown } from "../../dropdown-options/reasons-for-shutdown";
import { lifespans } from "../../dropdown-options/lifespans";
import { fundingRounds } from "../../dropdown-options/funding-rounds";
import { fundingAmounts } from "../../dropdown-options/funding-amounts";
import { genders } from "../../dropdown-options/genders";
import { noOfFounders } from "../../dropdown-options/no-of-founders";
import { useSupabaseApp } from "../../../SupabaseApp";

/**
 * Locations Filter Component
 * 
 * Analyzes geographic distribution of startups across countries/regions.
 * Shows startup frequency by location with top 20 countries displayed.
 * Filters by entity type, shutdown reasons, lifespan, industry, and founder demographics.
 * Includes view toggle to show all locations vs top 20.
 * 
 * @param {Object} props - Component props including Supabase client
 * @param {Object} props.supabase - Supabase client instance
 * @returns {JSX.Element} Geographic distribution chart
 */
const Locations = (props) => {
  // State to control alert visibility
  const [alert, setAlert] = useState(false);

  // State to store alert message content
  const [message, setMessage] = useState("");

  // Close alert and clear message
  function closeAlert() {
    setAlert(false);
    setMessage("");
  }

  // Get current user and Supabase client from context
  const { currentUser, supabase } = useSupabaseApp();

  // Check if user is authenticated; redirect if not
  if (!currentUser) {
    throw new Error(`Must be logged in`);
  }

  // State to manage filter form inputs
  const [filters, setFilters] = useState({
    type: "Location",
    filtersView: true,
    entityType: "Entity type",
    locationsNum: "No. of locations",
    sliceNum: "",
    reasons: "Reasons for shutdown",
    lifespan: "Lifespan",
    industry: "Industry",
    fundingRound: "Funding round",
    fundingAmount: "Funding amount",
    gender: "Gender",
    numOfFounders: "No. of founders",
  });

  // hook for results
  const [investments, setInvestments] = useState([
    {
      Location: "United States",
      Frequency: 12119,
      "%": 57.5,
    },
    {
      Location: "United Kingdom",
      Frequency: 1541,
      "%": 7.3,
    },
    {
      Location: "India",
      Frequency: 1140,
      "%": 5.4,
    },
    {
      Location: "Canada",
      Frequency: 677,
      "%": 3.2,
    },
    {
      Location: "Brazil",
      Frequency: 394,
      "%": 1.9,
    },
    {
      Location: "Australia",
      Frequency: 364,
      "%": 1.7,
    },
    {
      Location: "Germany",
      Frequency: 358,
      "%": 1.7,
    },
    {
      Location: "Spain",
      Frequency: 339,
      "%": 1.6,
    },
    {
      Location: "France",
      Frequency: 292,
      "%": 1.4,
    },
    {
      Location: "Ireland",
      Frequency: 264,
      "%": 1.3,
    },
    {
      Location: "Israel",
      Frequency: 256,
      "%": 1.2,
    },
    {
      Location: "China",
      Frequency: 246,
      "%": 1.2,
    },
    {
      Location: "Malaysia",
      Frequency: 246,
      "%": 1.2,
    },
    {
      Location: "Turkey",
      Frequency: 182,
      "%": 0.9,
    },
    {
      Location: "Singapore",
      Frequency: 174,
      "%": 0.8,
    },
    {
      Location: "Netherlands",
      Frequency: 160,
      "%": 0.8,
    },
    {
      Location: "South Korea",
      Frequency: 130,
      "%": 0.6,
    },
    {
      Location: "Russia",
      Frequency: 110,
      "%": 0.5,
    },
    {
      Location: "Italy",
      Frequency: 102,
      "%": 0.5,
    },
    {
      Location: "Sweden",
      Frequency: 96,
      "%": 0.5,
    },
  ]);

  // Reset all filter form inputs to their default values and close any alert
  function resetFilters(e) {
    e.preventDefault();
    setFilters({
      type: "Location",
      filtersView: true,
      entityType: "Entity type",
      locationsNum: "No. of locations",
      sliceNum: "",
      reasons: "Reasons for shutdown",
      lifespan: "Lifespan",
      industry: "Industry",
      fundingRound: "Funding round",
      fundingAmount: "Funding amount",
      gender: "Gender",
      numOfFounders: "No. of founders",
    });
    setAlert(false);
  }

  // Update filter state with special handling for location count and view toggle
  function changeFilters(e) {
    e.preventDefault();

    let name = e.target.name;
    let value = e.target.value;

    let name2, value2, name3, value3;

    // Handle location count selection - sets view toggle and slice number for top-N display
    if (name === "locationsNum" && value === "No. of locations") {
      name2 = "filtersView";
      value2 = true;
    } else if (name === "locationsNum" && value !== "No. of locations") {
      name2 = "filtersView";
      value2 = false;

      // Set slice number based on selected top-N option
      if (value === "Top 10") {
        name3 = "sliceNum";
        value3 = "10";
      }

      if (value === "Top 20") {
        name3 = "sliceNum";
        value3 = "20";
      }

      if (value === "Top 30") {
        name3 = "sliceNum";
        value3 = "30";
      }
    }

    // Create a copy of current filters object
    let filtersCopy = { ...filters };

    // Update the changed filter properties
    filtersCopy[name] = value;
    filtersCopy[name2] = value2;
    filtersCopy[name3] = value3;
    setFilters({ ...filtersCopy });
  }

  // function to submit form
  function submitFilters(e) {
    setAlert(true);
    setMessage(
      <div style={{ display: "flex", paddingTop: "10px" }}>
        <div style={{ width: "90%" }}>
          <p style={{ color: "#0f9d58" }}>
            <BiAnalyse className="alert-icon" /> Loading
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            Won't be long now. We're always doing our best to reduce waiting
            time.
          </p>
          <p style={{ marginLeft: "28px", fontSize: "14px" }}>
            If your waiting time is too long,{" "}
            <a
              href="https://www.roiquant.com/contact"
              target="_blank"
              rel="noopener noreferrer"
            >
              contact us
            </a>
            .
          </p>
        </div>
        <div style={{ width: "10%", textAlign: "right" }}>
          <Spinner animation="border" variant="secondary" />
        </div>
      </div>
    );

    supabase.functions
      .invoke("postmortemAnalysis", { body: filters })
      .then((response) => {
        let graphData = response.data;
        let slicedData = graphData.slice(0, filters.sliceNum);
        setInvestments(slicedData);
        closeAlert();
      })
      .catch((error) => {
        setAlert(true);
        setMessage(
          <div style={{ paddingTop: "10px" }}>
            <p style={{ color: "red" }}>
              <AiOutlineCloseCircle className="alert-icon" /> Error
            </p>
            <p style={{ marginLeft: "28px" }}>
              Your filter query returned no results. Please change your filter
              values and try again.
            </p>
          </div>
        );
      });
    e.preventDefault();
  }

  return (
    <div className="accordion-card-body">
      {/* loading alert */}

      {alert ? (
        <Alert className="error-message floating-alert" variant="light">
          <div>
            <div style={{ textAlign: "right" }}>
              <Button className="pitchdeckmessageclose" onClick={closeAlert}>
                <IoMdClose />
              </Button>
            </div>
            {message}
          </div>
        </Alert>
      ) : (
        ""
      )}

      <div className="col-12 div-filter">
        {/* form */}
        <Form onSubmit={submitFilters}>
          <Form.Group className="inputgroup">
            <Row className="align-items-center">
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.locationsNum}
                  name="locationsNum"
                  onChange={changeFilters}
                >
                  <option>No. of locations</option>
                  <option>Top 10</option>
                  <option>Top 20</option>
                  <option>Top 30</option>
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.entityType}
                  disabled={filters.filtersView}
                  name="entityType"
                  onChange={changeFilters}
                >
                  <option>Entity type</option>
                  {entityTypes.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.reasons}
                  disabled={filters.filtersView}
                  name="reasons"
                  onChange={changeFilters}
                >
                  <option>Reasons for shutdown</option>
                  {reasonsForShutdown.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.lifespan}
                  disabled={filters.filtersView}
                  name="lifespan"
                  onChange={changeFilters}
                >
                  <option>Lifespan</option>
                  {lifespans.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.industry}
                  disabled={filters.filtersView}
                  name="industry"
                  onChange={changeFilters}
                >
                  <option>Industry</option>
                  {industriesNew.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.fundingRound}
                  disabled={filters.filtersView}
                  name="fundingRound"
                  onChange={changeFilters}
                >
                  <option>Funding round</option>
                  {fundingRounds.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.fundingAmount}
                  disabled={filters.filtersView}
                  name="fundingAmount"
                  onChange={changeFilters}
                >
                  <option>Funding amount</option>
                  {fundingAmounts.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.gender}
                  disabled={filters.filtersView}
                  name="gender"
                  onChange={changeFilters}
                >
                  <option>Gender</option>
                  {genders.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              <Col xs="auto" className="filter-dropdown">
                <Form.Select
                  value={filters.numOfFounders}
                  disabled={filters.filtersView}
                  name="numOfFounders"
                  onChange={changeFilters}
                >
                  <option>No. of founders</option>
                  {noOfFounders.map((option) => (
                    <option>{option.key}</option>
                  ))}
                </Form.Select>
              </Col>
              {/* reset button */}
              <Col xs="auto">
                <button
                  onClick={resetFilters}
                  className="reset-button tour-clear-filter"
                >
                  <span>Clear</span>
                </button>
              </Col>
              {/* submit button */}
              <Col xs="auto">
                <button type="submit" className="submit-button">
                  <span>Filter</span>
                </button>
              </Col>
            </Row>
          </Form.Group>
        </Form>
      </div>

      {/* results */}
      <div className="col-12">
        <Card className="graph-card">
          <div>
            {/* chart component */}
            <ResponsiveContainer id="vertical-charts">
              <BarChart
                data={investments}
                margin={{ top: 20, right: 20, left: 100, bottom: 50 }}
                width={1100}
                height={1100}
                fontFamily="AileronRegular"
                layout={"vertical"}
              >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type={"number"}
                  orientation={"bottom"}
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  label={{
                    value: "Frequency",
                    offset: -40,
                    position: "insideBottom",
                  }}
                />
                <YAxis
                  type={"category"}
                  orientation={"left"}
                  dataKey="Location"
                  interval={0}
                  fontSize="15px"
                  fontFamily="AileronRegular"
                  label={{
                    value: "Locations",
                    angle: -90,
                    offset: -40,
                    position: "insideLeft",
                  }}
                />
                <Tooltip />
                <Bar dataKey="Frequency" fill="#8884d8" barSize={50}>
                  <LabelList dataKey="%" position="right" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* notes */}
      <div className="col-12 extra-notes">
        <span className="info-notes">
          Note:
          <ul>
            <li id="notes-points">
              Based on{" "}
              {props.companiesWithLocation
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies with recorded{" "}
              <span style={{ fontFamily: "AileronItalic" }}>location data</span>
              .
            </li>
            <li id="notes-points">
              In due time, the remaining{" "}
              {props.companiesWithoutLocation
                .toString()
                .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}{" "}
              inactive companies without location data will be identified and
              recorded by our algorithms.
            </li>
          </ul>
        </span>
      </div>
    </div>
  );
};

export default Locations;
